import { useState } from 'react';
import { BordButton, BordFeedbackAlert } from 'bord-design-system';
import BordUploaderButton from 'components/NudosComponents/BordUploaderButton/BordUploaderButton';
import {
  ACCEPTED_FILE_EXTENSION,
  IDocumentDetail
} from 'components/NudosComponents/BordUploaderButton/BordUploaderButton.types';
import { ICreateCatalogProductBody, INewToolFormData, IToolSearchResult } from 'types/quoteEditor.types';
import { PRODUCT_CATEGORY } from 'types/category';
import { createImage } from 'services/cataloge';
import { QuoteEditorService } from 'services/quote-editor.service';
import { displayErrorNotification } from 'utils/displayNudosStandardNotifications';
import BordStatusBadge from 'components/NudosComponents/BordStatusBadge/BordStatusBadge';
import CategoryDropdown from './components/CategoryDropdown/CategoryDropdown';
import BrandDropdown from './components/BrandDropdown/BrandDropdown';
import ProfileDropdown from './components/ProfileDropdown/ProfileDropdown';
import PositionDropdown from './components/PositionDropdown/PositionDropdown';
import QuoteNewToolLaptopSpecs from './components/QuoteNewToolLaptopSpecs/QuoteNewToolLaptopSpecs';
import QuoteNewToolMonitorSpecs from './components/QuoteNewToolMonitorSpecs/QuoteNewToolMonitorSpecs';
import QuoteNewToolFurnitureSpecs from './components/QuoteNewToolFurnitureSpecs/QuoteNewToolFurnitureSpecs';
import { bordColorTheme } from '../../../../../styles/styles.constants';
import './QuoteNewToolForm.scss';

interface IQuoteNewToolForm {
  countryCode: string;
  quoteItemId: number;
  onSave: (tool: IToolSearchResult) => void;
  onCancel: () => void;
}

const LAPTOP_CATEGORIES = [
  PRODUCT_CATEGORY.LAPTOP,
  PRODUCT_CATEGORY.PC,
  PRODUCT_CATEGORY.DEP_LAPTOPS,
  PRODUCT_CATEGORY.CTO_LAPTOPS
];
const MONITOR_CATEGORIES = [PRODUCT_CATEGORY.MONITORS];
const FURNITURE_CATEGORIES = [PRODUCT_CATEGORY.FURNITURE, PRODUCT_CATEGORY.DESKS, PRODUCT_CATEGORY.CHAIRS];

/**
 * Formulario de creación de una herramienta nueva de catálogo desde el flujo de cotización.
 * La herramienta resultante queda en estado Apagado y solo es accesible desde esta cotización.
 */
const QuoteNewToolForm = ({ countryCode, quoteItemId, onSave, onCancel }: IQuoteNewToolForm) => {
  const emptyForm: INewToolFormData = {
    category: '',
    commercialName: '',
    brand: '',
    guarantee: '',
    longDescription: '',
    photo: null
  };

  const [activeTab, setActiveTab] = useState<'general' | 'specifications'>('general');
  const [formData, setFormData] = useState<INewToolFormData>(emptyForm);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<IDocumentDetail | undefined>();

  const isLaptop = LAPTOP_CATEGORIES.includes(formData.category as PRODUCT_CATEGORY);
  const isMonitor = MONITOR_CATEGORIES.includes(formData.category as PRODUCT_CATEGORY);
  const isFurniture = FURNITURE_CATEGORIES.includes(formData.category as PRODUCT_CATEGORY);
  const hasSpecs = isLaptop || isMonitor || isFurniture;

  const laptopSpecsValid =
    isLaptop &&
    !!formData.processor &&
    !!formData.hardDrive &&
    !!formData.ram &&
    !!formData.displayInches &&
    !!formData.software;
  const monitorSpecsValid = isMonitor && !!formData.displayInches && !!formData.pixels && !!formData.fps;
  const furnitureSpecsValid = isFurniture && !!formData.material;
  const noSpecsRequired = !hasSpecs;

  const specsValid = laptopSpecsValid || monitorSpecsValid || furnitureSpecsValid || noSpecsRequired;
  const canSave =
    !!formData.commercialName &&
    !!formData.categoryId &&
    !!formData.brandId &&
    !!formData.imageId &&
    !!formData.profileIds?.length &&
    !!formData.positionId &&
    !!formData.color &&
    specsValid &&
    !isSaving;

  /** Maneja el cambio de categoría: actualiza el form, limpia specs/marca y color. */
  const handleCategoryChange = (categoryId: number, categoryName: string) => {
    setFormData(prev => ({
      ...prev,
      category: categoryName,
      categoryId,
      brand: '',
      brandId: undefined,
      processor: undefined,
      hardDrive: undefined,
      ram: undefined,
      displayInches: undefined,
      software: undefined,
      inBoxAccessories: undefined,
      resolution: undefined,
      pixels: undefined,
      fps: undefined,
      material: undefined,
      color: undefined
    }));
  };

  /** Sube la foto al servicio de imágenes y guarda el imageId resultante. */
  const handlePhotoUpload = async (file: File) => {
    setIsUploadingPhoto(true);
    try {
      const dataImg = await createImage(file);
      setFormData(prev => ({ ...prev, imageId: dataImg.data.catalogImageId, photo: file }));
      setUploadedFile({ documentName: file.name, name: file.name });
    } catch {
      displayErrorNotification();
    } finally {
      setIsUploadingPhoto(false);
    }
  };

  /** Elimina la foto seleccionada del estado del formulario. */
  const handleDeletePhoto = () => {
    setFormData(prev => ({ ...prev, imageId: undefined, photo: null }));
    setUploadedFile(undefined);
  };

  /** Actualiza un campo de especificaciones técnicas en el formulario. */
  const handleSpecChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  /** Envía el formulario al endpoint de creación y propaga el producto resultante al padre. */
  const handleSave = async () => {
    if (!formData.categoryId || !formData.brandId || !formData.imageId) return;

    const body: ICreateCatalogProductBody = {
      countryCode,
      quoteItemId,
      commercialName: formData.commercialName,
      brandId: formData.brandId,
      description: formData.longDescription || formData.commercialName,
      imageId: formData.imageId,
      categoryId: formData.categoryId,
      dataSheet: '',
      forceCreationOfRepeatedSku: false,
      profileIds: formData.profileIds,
      positionId: formData.positionId,
      ...(formData.longDescription && { longDescription: formData.longDescription }),
      ...(formData.processor && { processor: formData.processor }),
      ...(formData.hardDrive && { hardDrive: formData.hardDrive }),
      ...(formData.ram && { ram: formData.ram }),
      ...(formData.displayInches && { displayInches: formData.displayInches }),
      ...(formData.software && { software: formData.software }),
      ...(formData.inBoxAccessories && { inBoxAccessories: formData.inBoxAccessories }),
      ...(formData.resolution && { resolution: formData.resolution }),
      ...(formData.pixels && { pixels: formData.pixels }),
      ...(formData.fps && { fps: formData.fps }),
      ...(formData.material && { material: formData.material }),
      ...(formData.color && { color: formData.color })
    };

    setIsSaving(true);
    await QuoteEditorService.createCatalogProductForQuoteItem(body)
      .then(created => {
        const tool: IToolSearchResult = {
          id: created.productId,
          name: created.commercialName,
          sku: created.uid,
          imageUrl: created.url ?? undefined,
          category: created.category ?? formData.category,
          categoryId: created.categoryId ?? undefined,
          brand: created.brand ?? formData.brand,
          isNew: true
        };
        onSave(tool);
      })
      .catch(() => displayErrorNotification())
      .finally(() => setIsSaving(false));
  };

  return (
    <div className="quoteNewToolForm">
      <BordFeedbackAlert
        type="warning"
        title="Herramienta de uso restringido"
        description="Al crearse desde este flujo quedará en estado Apagado y solo será accesible desde esta cotización. No tendrá precios ni estará visible para otros clientes. Para modificar estos aspectos ve a la herramienta desde Catálogo."
        className="restrictedWarningAlert"
      />
      <div className="formControls">
        <CategoryDropdown value={formData.category} onChange={handleCategoryChange} />
        <BordStatusBadge
          label="Prendido solo para Cargue"
          dotColor={bordColorTheme.white[400]}
          textColor={bordColorTheme.white[400]}
        />
      </div>
      {!!formData.categoryId && (
        <>
          <div className="formTabs">
            <button
              className={`tabBtn${activeTab === 'general' ? ' active' : ''}`}
              onClick={() => setActiveTab('general')}
            >
              General
            </button>
            <button
              className={`tabBtn${activeTab === 'specifications' ? ' active' : ''}`}
              onClick={() => setActiveTab('specifications')}
            >
              Especificaciones
            </button>
          </div>
          {activeTab === 'general' && (
            <div className="generalTab">
              <div className="fieldGroup">
                <label className="fieldLabel">Foto de la herramienta*</label>
                <BordUploaderButton
                  title={
                    <p className="mb-0">
                      <span className="uploadLink">Busca</span> o arrastra la foto de la herramienta
                    </p>
                  }
                  emptyBordUploaderButtonSubtitle="Formatos aceptados: JPG, PNG · Peso máximo: 2 MB"
                  acceptFileExtension={[
                    ACCEPTED_FILE_EXTENSION.JPG,
                    ACCEPTED_FILE_EXTENSION.JPEG,
                    ACCEPTED_FILE_EXTENSION.PNG
                  ]}
                  isFilled={!!formData.imageId}
                  defaultFile={uploadedFile}
                  modeReplaceFile
                  loading={isUploadingPhoto}
                  handleChangeFile={handlePhotoUpload}
                  handleDeleteFile={handleDeletePhoto}
                />
              </div>
              <div className="fieldGroup">
                <label className="fieldLabel">Nombre comercial*</label>
                <input
                  className="textField"
                  placeholder="Agregar nombre comercial"
                  value={formData.commercialName}
                  onChange={e => setFormData(prev => ({ ...prev, commercialName: e.target.value }))}
                />
              </div>
              <div className="fieldRow twoCol">
                <div className="fieldGroup">
                  <BrandDropdown
                    value={formData.brand}
                    categoryId={formData.categoryId}
                    onChange={(id, name) => setFormData(prev => ({ ...prev, brand: name, brandId: id }))}
                  />
                </div>
                <div className="fieldGroup">
                  <ProfileDropdown
                    value={formData.profileIds ?? []}
                    onChange={profileIds => setFormData(prev => ({ ...prev, profileIds }))}
                  />
                </div>
              </div>
              <div className="fieldRow twoCol">
                <div className="fieldGroup">
                  <PositionDropdown
                    value={formData.position ?? ''}
                    onChange={(id, name) => setFormData(prev => ({ ...prev, position: name, positionId: id }))}
                  />
                </div>
                <div className="fieldGroup">
                  <label className="fieldLabel">Garantía (opcional)</label>
                  <input
                    className="textField"
                    placeholder="Ej. 2 años"
                    value={formData.guarantee}
                    onChange={e => setFormData(prev => ({ ...prev, guarantee: e.target.value }))}
                  />
                </div>
              </div>
              <div className="fieldGroup">
                <label className="fieldLabel">Descripción larga (opcional máx. 400 caracteres)</label>
                <textarea
                  className="textAreaField"
                  placeholder="Descripción del producto"
                  maxLength={400}
                  value={formData.longDescription}
                  onChange={e => setFormData(prev => ({ ...prev, longDescription: e.target.value }))}
                />
                <span className="charCounter">{formData.longDescription.length}/400</span>
              </div>
            </div>
          )}
          {activeTab === 'specifications' && (
            <div className="specificationsTab">
              <div className="specField">
                <label className="fieldLabel">Color*</label>
                <input
                  className="specInput"
                  placeholder="Ej. Space Gray"
                  value={formData.color ?? ''}
                  onChange={e => setFormData(prev => ({ ...prev, color: e.target.value }))}
                />
              </div>
              {isLaptop && (
                <QuoteNewToolLaptopSpecs
                  processor={formData.processor}
                  hardDrive={formData.hardDrive}
                  ram={formData.ram}
                  displayInches={formData.displayInches}
                  software={formData.software}
                  inBoxAccessories={formData.inBoxAccessories}
                  onChangeSpec={handleSpecChange}
                />
              )}
              {isMonitor && (
                <QuoteNewToolMonitorSpecs
                  displayInches={formData.displayInches}
                  pixels={formData.pixels}
                  resolution={formData.resolution}
                  fps={formData.fps}
                  onChangeSpec={handleSpecChange}
                />
              )}
              {isFurniture && (
                <QuoteNewToolFurnitureSpecs material={formData.material} onChangeSpec={handleSpecChange} />
              )}
              {!hasSpecs && <p className="noSpecsMessage">Esta categoría no requiere especificaciones técnicas.</p>}
            </div>
          )}
          <div className="formActions">
            <BordButton label="Cancelar" bordButtonStyle="outlined" onClick={onCancel} />
            <BordButton label="Guardar herramienta" disabled={!canSave} onClick={handleSave} />
          </div>
        </>
      )}
    </div>
  );
};

export default QuoteNewToolForm;
